import React from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../theme/ThemeContext';
import ThemedText from '../components/common/ThemedText';

const PrivacyPolicyScreen: React.FC = () => {
  const navigation = useNavigation();
  const { theme } = useTheme();

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={Platform.OS === 'web' ? [] : ['top', 'left', 'right']}>
      <View style={[styles.header, { backgroundColor: theme.backgroundSecondary, borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Privacy Policy</ThemedText>
        <View style={styles.headerRight} />
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator
      >
        <View style={styles.section}>
          <ThemedText style={[styles.lastUpdated, { color: theme.textSecondary }]}>
            Last Updated: January 2026
          </ThemedText>

          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            LNM Global Pvt Ltd ("we", "our", or "us") operates the LNB mobile application. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our application.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>1. Information We Collect</ThemedText>
          
          <ThemedText style={[styles.subheading, { color: theme.text }]}>Personal Information</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            We collect information that you provide directly to us, including:{'\n'}
            • Name and contact information (phone number, email){'\n'}
            • Professional details (company, designation, industry){'\n'}
            • Business information and profile details{'\n'}
            • User-generated content (posts, comments, messages){'\n'}
            • Photos and documents you choose to share{'\n'}
            • Contact list (only when you use Find Friends feature)
          </ThemedText>

          <ThemedText style={[styles.subheading, { color: theme.text }]}>Device Permissions</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            The app requires the following permissions:{'\n'}
            • Storage Access: To select profile photos and documents from your device{'\n'}
            • Push Notifications: To send you ledger updates, club notifications, and messages{'\n'}
            • Contacts Access: To help you find friends already using the app (optional)
          </ThemedText>

          <ThemedText style={[styles.subheading, { color: theme.text }]}>Automatically Collected Information</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            When you use our application, we may automatically collect:{'\n'}
            • Device information (device type, operating system){'\n'}
            • Usage data (features accessed, time spent){'\n'}
            • Log data (IP address, access times){'\n'}
            • Push notification tokens (for Firebase Cloud Messaging)
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>2. How We Use Your Information</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            We use the collected information to:{'\n'}
            • Provide and maintain our services (ledger management, networking, clubs){'\n'}
            • Create and manage your account{'\n'}
            • Connect you with other professionals through our network{'\n'}
            • Send push notifications for ledger updates, club activities, and messages{'\n'}
            • Enable document and photo sharing within the platform{'\n'}
            • Help you find friends who are already using the app (with your permission){'\n'}
            • Improve user experience and app functionality{'\n'}
            • Detect and prevent fraud or abuse{'\n'}
            • Comply with legal obligations
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>3. Information Sharing</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            We do nGoogle Firebase for authentication and push notifications{'\n'}
            • With Google Cloud Platform for data storage and hosting{'\n'}
            • With service providers who assist in our operations (under strict confidentiality){'\n'}
            • When required by law or legal process{'\n'}
            • To protect our rights and prevent fraud
          </ThemedText>

          <ThemedText style={[styles.subheading, { color: theme.text }]}>Third-Party Services</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            We use the following third-party services:{'\n'}
            • Firebase Authentication: For secure phone number authentication{'\n'}
            • Firebase Cloud Messaging: For push notifications{'\n'}
            • Google Cloud Storage: For storing profile photos and documents{'\n'}
            • Google Cloud SQL: For secure database hostingr operations{'\n'}
            • When required by law or legal process{'\n'}
            • To protect our rights and prevent fraud
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>4. Data Security</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>5. Data Retention</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            We retain your personal information for as long as necessary to provide our services and comply with legal obligations. You may request deletion of your account and associated data at any time through the app settings.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>6. Your Rights</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            Under applicable Indian laws, including the Information Technology Act, 2000, you have the right to:{'\n'}
            • Access your personal information{'\n'}
            • Correct inaccurate data{'\n'}
            • Request deletion of your data{'\n'}
            • Withdraw consent for data processing{'\n'}
            • File complaints with relevant authorities
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>7. Children's Privacy</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            Our services are not intended for users under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal information, please contact us.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>8. Changes to Privacy Policy</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy in the app. Changes are effective when posted.
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>9. Contact Us</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            If you have questions about this Privacy Policy, please contact us at:{'\n\n'}
            LNM Global Pvt Ltd{'\n'}
            Email: posteditorlnf@gmail.com
          </ThemedText>

          <ThemedText style={[styles.heading, { color: theme.text }]}>10. Governing Law</ThemedText>
          <ThemedText style={[styles.paragraph, { color: theme.text }]}>
            This Privacy Policy is governed by and construed in accordance with the laws of India. Any disputes arising from this policy shall be subject to the exclusive jurisdiction of the courts in [City], India.
          </ThemedText>
        </View>

        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    ...(Platform.OS === 'web' ? { position: 'sticky' as any, top: 0, zIndex: 10 } : {}),
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  headerRight: {
    width: 32,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 100,
  },
  section: {
    padding: 20,
  },
  lastUpdated: {
    fontSize: 12,
    marginBottom: 16,
    fontStyle: 'italic',
  },
  heading: {
    fontSize: 18,
    fontWeight: '700',
    marginTop: 24,
    marginBottom: 12,
  },
  subheading: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  paragraph: {
    fontSize: 14,
    lineHeight: 22,
    marginBottom: 12,
  },
  bottomPadding: {
    height: 40,
  },
});

export default PrivacyPolicyScreen;
